import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Button,
  Table,
  Badge,
  Tabs,
  Modal,
  Stack,
  Select,
  Textarea,
  Text,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconEye } from '@tabler/icons-react';
import { useRMAList, useRMADetail, useCreateRMA } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  authorized: 'blue',
  received: 'cyan',
  completed: 'green',
  rejected: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  quality: 'red',
  damaged: 'orange',
  wrong_item: 'yellow',
  overstock: 'blue',
  recall: 'grape',
  other: 'gray',
};

export default function ReturnsPage() {
  const [activeTab, setActiveTab] = useState<string | null>('all');
  const [createOpened, createHandlers] = useDisclosure(false);
  const [detailOpened, detailHandlers] = useDisclosure(false);
  const [selectedRMAId, setSelectedRMAId] = useState<number | null>(null);
  const [returnType, setReturnType] = useState<string>('');
  const [notes, setNotes] = useState('');

  const statusFilter = activeTab === 'all' ? undefined : activeTab ?? undefined;
  const { data: rmas, isLoading } = useRMAList(statusFilter);
  const { data: rmaDetail } = useRMADetail(selectedRMAId);
  const createRMA = useCreateRMA();

  const handleViewDetail = (id: number) => {
    setSelectedRMAId(id);
    detailHandlers.open();
  };

  const handleCreateRMA = async () => {
    if (!returnType) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a return type',
        color: 'red',
      });
      return;
    }

    try {
      await createRMA.mutateAsync({
        return_type: returnType,
        notes,
      });
      notifications.show({
        title: 'Success',
        message: 'Return request submitted successfully',
        color: 'green',
      });
      createHandlers.close();
      setReturnType('');
      setNotes('');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to submit return request',
        color: 'red',
      });
    }
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Returns & RMA</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={createHandlers.open}>
          Request Return
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="all">All</Tabs.Tab>
          <Tabs.Tab value="pending">Pending</Tabs.Tab>
          <Tabs.Tab value="authorized">Authorized</Tabs.Tab>
          <Tabs.Tab value="received">Received</Tabs.Tab>
          <Tabs.Tab value="completed">Completed</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={activeTab || 'all'} pt="md">
          {isLoading ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : !rmas || rmas.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              No returns found
            </Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>RMA #</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Items</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rmas.map((rma: any) => (
                  <Table.Tr key={rma.id}>
                    <Table.Td>RMA-{rma.id}</Table.Td>
                    <Table.Td>
                      <Badge color={TYPE_COLORS[rma.return_type]} variant="light">
                        {rma.return_type.replace('_', ' ')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{new Date(rma.created_at).toLocaleDateString()}</Table.Td>
                    <Table.Td>{rma.items_count || 0}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLORS[rma.status]} variant="light">
                        {rma.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconEye size={14} />}
                        onClick={() => handleViewDetail(rma.id)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>
      </Tabs>

      <Modal opened={createOpened} onClose={createHandlers.close} title="Request Return" size="md">
        <Stack>
          <Select
            label="Return Type"
            placeholder="Select reason for return"
            data={[
              { value: 'quality', label: 'Quality Issue' },
              { value: 'damaged', label: 'Damaged' },
              { value: 'wrong_item', label: 'Wrong Item' },
              { value: 'overstock', label: 'Overstock' },
              { value: 'recall', label: 'Recall' },
              { value: 'other', label: 'Other' },
            ]}
            value={returnType}
            onChange={(value) => setReturnType(value || '')}
            required
          />
          <Textarea
            label="Notes"
            placeholder="Provide details about the return..."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={4}
          />
          <Button onClick={handleCreateRMA} loading={createRMA.isPending}>
            Submit Return Request
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={detailOpened}
        onClose={detailHandlers.close}
        title={`RMA-${selectedRMAId} Details`}
        size="md"
      >
        {rmaDetail ? (
          <Stack>
            <Group>
              <Text fw={600}>Status:</Text>
              <Badge color={STATUS_COLORS[rmaDetail.status]} variant="light">
                {rmaDetail.status}
              </Badge>
            </Group>
            <Group>
              <Text fw={600}>Type:</Text>
              <Badge color={TYPE_COLORS[rmaDetail.return_type]} variant="light">
                {rmaDetail.return_type.replace('_', ' ')}
              </Badge>
            </Group>
            <div>
              <Text fw={600}>Date Requested:</Text>
              <Text>{new Date(rmaDetail.created_at).toLocaleString()}</Text>
            </div>
            {rmaDetail.notes && (
              <div>
                <Text fw={600}>Notes:</Text>
                <Text>{rmaDetail.notes}</Text>
              </div>
            )}
            {rmaDetail.timeline && rmaDetail.timeline.length > 0 && (
              <div>
                <Text fw={600} mb="xs">
                  Timeline:
                </Text>
                <Stack gap="xs">
                  {rmaDetail.timeline.map((event: any, idx: number) => (
                    <Group key={idx}>
                      <Text size="sm" c="dimmed">
                        {new Date(event.timestamp).toLocaleString()}
                      </Text>
                      <Text size="sm">{event.description}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>
            )}
          </Stack>
        ) : (
          <Skeleton height={200} />
        )}
      </Modal>
    </Container>
  );
}
